'use client';

import { useState, useEffect, useMemo, Fragment } from 'react';
import { apiClient } from '@/app/api/client';
import { Dialog, Transition, Tab } from '@headlessui/react';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { Input, Label, Select, Button, Field, Fieldset, Dropdown } from '@/components/forms';
import { blogSpotPublishingService } from '@/services/blogspot-publishing';
import { BlogSpotConnection } from '@/services/blogspot-publishing';
import { CheckCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export interface TestLog {
  id: string;
  timestamp: Date;
  level: 'info' | 'success' | 'error' | 'warning';
  message: string;
  details?: string;
}

interface ConnectionType {
  type: string;
  displayName: string;
  authType: 'oauth' | 'token' | 'application_password';
  requiredSettings: { key: string; label: string; type: string; options?: string[]; defaultValue?: string; placeholder?: string; hidden?: boolean }[];
  iconUrl: string;
}

interface Connection {
  id: string;
  platformName: string;
  platformType: string;
  iconUrl: string;
  settings?: any;
}

interface ConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  connectionData?: Connection | ConnectionType | null;
}

export default function ConnectionModal({ isOpen, onClose, onSave, connectionData }: ConnectionModalProps) {
  const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>([]);
  const [formData, setFormData] = useState<Partial<Connection>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authResult, setAuthResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isTestingMode, setIsTestingMode] = useState(false);
  const [testLogs, setTestLogs] = useState<TestLog[]>([]);
  const [copySuccess, setCopySuccess] = useState(false);
  const [availableSites, setAvailableSites] = useState<Array<{ id: string; name: string; url: string }>>([]);
  const [showSiteSelector, setShowSiteSelector] = useState(false);
  const [selectedSite, setSelectedSite] = useState<{ id: string; name: string; url: string } | null>(null);

  const selectedTypeDetails = useMemo(() => {
    return connectionTypes.find(ct => ct.type === formData.platformType);
  }, [formData.platformType, connectionTypes]);

  const isEditingConnection = useMemo(() => {
    return connectionData && 'id' in connectionData;
  }, [connectionData]);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const { data } = await apiClient.get('/api/connections/types');
        setConnectionTypes(data || []);
      } catch (err) {
        console.error('Failed to load connection types', err);
      }
    };
    fetchTypes();
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Reset all state when dialog opens
      setError(null);
      setTestResult(null);
      setAuthResult(null);
      setIsTesting(false);
      setIsAuthorizing(false);
      setIsTestingMode(false);
      setTestLogs([]);
      setCopySuccess(false);
      setIsSaving(false);

      if (connectionData && 'id' in connectionData) {
        setFormData({ ...connectionData, settings: connectionData.settings || {} });
      } else if (connectionData && 'type' in connectionData) {
        const typeData = connectionData as ConnectionType;
        setFormData({ platformType: typeData.type, platformName: typeData.displayName, settings: {} });
      } else {
        setFormData({ platformType: 'custom_http', platformName: '', settings: {} });
      }
    }
  }, [isOpen, connectionData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name.startsWith('settings.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSettingChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
  };

  const handleDropdownChange = (name: string, value: string) => {
    if (name.startsWith('settings.')) {
      const key = name.split('.')[1];
      setFormData(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      // Apply defaults for hidden settings just before submit (do not render UI for them)
      const hiddenSettings = (selectedTypeDetails?.requiredSettings || []).filter(s => s.hidden);
      const settingsWithHidden: any = { ...(formData.settings || {}) };
      for (const s of hiddenSettings) {
        if (settingsWithHidden[s.key] === undefined) {
          settingsWithHidden[s.key] = s.defaultValue ?? '';
        }
      }

      const payload = { ...formData, iconUrl: selectedTypeDetails?.iconUrl, settings: settingsWithHidden };
      if (connectionData && 'id' in connectionData) {
        await apiClient.put(`/api/connections/${connectionData.id}`, payload);
      } else {
        await apiClient.post('/api/connections', payload);
      }
      onSave();
    } catch (err: any) {
      setError(err.message || 'Failed to save connection.');
    } finally {
      setIsSaving(false);
    }
  };

  const addTestLog = (level: TestLog['level'], message: string, details?: string) => {
    const log: TestLog = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      level,
      message,
      details
    };
    setTestLogs(prev => [...prev, log]);
  };

  const copyLogsToClipboard = async () => {
    const logText = testLogs.map(log => {
      const timestamp = log.timestamp.toLocaleString();
      const level = log.level.toUpperCase();
      let text = `[${timestamp}] ${level}: ${log.message}`;
      if (log.details) {
        text += `\n  Details: ${log.details}`;
      }
      return text;
    }).join('\n');

    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(logText);
      } else {
        // Fallback for older browsers or non-HTTPS
        const textArea = document.createElement('textarea');
        textArea.value = logText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
      alert('Failed to copy logs. Please check browser console.');
    }
  };

  const handleTestConnection = async () => {
    if (!formData.platformType) return;

    setIsTestingMode(true);
    setIsTesting(true);
    setTestResult(null);
    setAuthResult(null);
    setError(null);
    setTestLogs([]);

    addTestLog('info', 'Starting connection test...');

    const typeDetails = connectionTypes.find(ct => ct.type === formData.platformType);

    if (typeDetails?.authType === 'oauth') {
      // For OAuth, testing means initiating the auth flow without saving.
      addTestLog('info', 'OAuth connection detected, starting authorization flow...');
      setIsAuthorizing(true);

      if (formData.platformType === 'blogspot') {
        await handleBlogSpotAuth(false); // `false` ensures it only tests, not saves
      } else if (formData.platformType === 'wordpress_oauth') {
        await handleWordPressAuth(false); // `false` ensures it only tests, not saves
      }
      return;
    } else if (formData.platformType === 'wordpress') {
      // For WordPress, use backend testing to avoid CORS issues
      addTestLog('info', 'Testing WordPress connection...');

      try {
        const { siteUrl, username, appPassword } = formData.settings;

        if (!siteUrl || !username || !appPassword) {
          addTestLog('error', 'Missing required fields');
          throw new Error('Please fill in all required fields: Site URL, Username, and Application Password');
        }

        // Validate and normalize URL format
        let normalizedUrl = siteUrl.trim();
        // Remove trailing slashes
        normalizedUrl = normalizedUrl.replace(/\/+$/, '');
        // Add https:// if no protocol
        if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
          normalizedUrl = 'https://' + normalizedUrl;
          addTestLog('info', `Added https:// prefix to URL: ${normalizedUrl}`);
        }

        addTestLog('info', `Testing connection to ${normalizedUrl}`);
        addTestLog('info', `Username: ${username}`);

        // Update settings with normalized URL
        const normalizedSettings = {
          ...formData.settings,
          siteUrl: normalizedUrl
        };

        // Use backend API to test (avoids CORS issues with WordPress.com)
        addTestLog('info', 'Testing via backend API...');
        const response = await apiClient.post(`/api/connections/test`, {
          platformName: 'wordpress',
          settings: normalizedSettings
        });

        if (response.data.success) {
          addTestLog('success', response.data.message || 'Connection test successful!');
          setTestResult({
            success: true,
            message: response.data.message || 'Successfully connected to WordPress'
          });
        } else {
          const errorMessage = response.data.message || 'Connection test failed';
          addTestLog('error', errorMessage);
          setTestResult({ success: false, message: errorMessage });
        }
      } catch (error: any) {
        console.error('WordPress test error:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Connection test failed';
        addTestLog('error', `Test failed: ${errorMessage}`);

        // Add helpful debugging info
        if (errorMessage.includes('401') || errorMessage.includes('Authentication')) {
          addTestLog('error', 'Authentication failed - check:');
          addTestLog('error', '1. Username is correct (not email address)');
          addTestLog('error', '2. Application Password is correct');
          addTestLog('error', '3. Application Password has not been revoked');
        } else if (errorMessage.includes('404') || errorMessage.includes('not found')) {
          addTestLog('error', 'WordPress REST API not found - check:');
          addTestLog('error', '1. Site URL is correct');
          addTestLog('error', '2. WordPress version is 4.7 or higher');
        }

        setTestResult({
          success: false,
          message: errorMessage
        });
      }
    } else {
      // For other connection types, use backend API
      addTestLog('info', `Testing ${formData.platformType} connection via backend...`);

      try {
        const response = await apiClient.post(`/api/connections/test`, {
          platformType: formData.platformType,
          settings: formData.settings
        });

        if (response.data.success) {
          addTestLog('success', 'Connection test successful!');
          setTestResult({ success: true, message: 'Connection test successful!' });
        } else {
          const errorMessage = response.data.message || 'Connection test failed';
          addTestLog('error', 'Connection test failed', errorMessage);
          setTestResult({ success: false, message: errorMessage });
        }
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Connection test failed';
        addTestLog('error', 'Connection test failed', errorMessage);
        setTestResult({
          success: false,
          message: errorMessage
        });
      }
    }

    setIsTesting(false);
  };

  const openHelpPage = () => {
    let hash = '';
    if (formData.platformType === 'blogspot') hash = '#blogspot';
    else if (formData.platformType === 'wordpress') hash = '#wordpress';
    else if (formData.platformType === 'wordpress_oauth') hash = '#wordpress-oauth';
    else if (formData.platformType === 'medium') hash = '#medium';
    else if (formData.platformType === 'custom_http') hash = '#custom';

    // Get www domain dynamically (replaces app/api subdomain with www)
    const wwwUrl = new URL(window.location.origin);
    wwwUrl.hostname = wwwUrl.hostname.replace(/^(app|api|[^.]+)\./, 'www.');
    const helpUrl = `${wwwUrl.origin}/help/connections${hash}`;

    window.open(helpUrl, '_blank', 'noopener,noreferrer');
  };

  const handleUpdateConnection = () => {
    if (!isEditingConnection) return;

    // Switch to log view to show progress
    setIsTestingMode(true);
    setTestLogs([]);
    setAuthResult(null);
    setTestResult(null);
    setError(null);

    // Handle OAuth connections
    if (formData.platformType === 'blogspot') {
      handleBlogSpotAuth(true);
    } else if (formData.platformType === 'wordpress_oauth') {
      handleWordPressAuth(true);
    } else {
      // For non-OAuth connections, just trigger a test
      handleTestConnection();
    }
  };

  const handleSiteSelection = async (site: { id: string; name: string; url: string }) => {
    addTestLog('info', `Selected site: ${site.name}`);

    // Update form data with selected site
    setFormData(prev => ({
      ...prev,
      settings: {
        ...prev.settings,
        siteId: site.id,
        siteName: site.name,
        siteUrl: site.url
      }
    }));

    setAuthResult({
      success: true,
      message: `Successfully connected to ${site.name}`
    });

    setShowSiteSelector(false);

    // Auto-save if this was a connect flow
    const andSave = !isEditingConnection || formData.settings?.accessToken;
    if (andSave) {
      addTestLog('info', 'Saving connection...');
      try {
        const payload = {
          ...formData,
          settings: {
            ...formData.settings,
            siteId: site.id,
            siteName: site.name,
            siteUrl: site.url
          },
          iconUrl: selectedTypeDetails?.iconUrl,
          platformName: site.name
        };

        if (connectionData && 'id' in connectionData) {
          await apiClient.put(`/api/connections/${connectionData.id}`, payload);
          addTestLog('success', 'Connection updated successfully!');
        } else {
          await apiClient.post('/api/connections', payload);
          addTestLog('success', 'Connection saved successfully!');
        }

        setTimeout(() => {
          onSave();
          onClose();
        }, 1500);
      } catch (error: any) {
        addTestLog('error', `Failed to save connection: ${error.message}`);
      }
    }
  };

  const handleBlogSpotAuth = async (andSave: boolean = false) => {
    if (!formData.settings?.clientId || !formData.settings?.clientSecret) {
      setAuthResult({ success: false, message: 'Please enter Client ID and Client Secret first' });
      return;
    }

    // When connecting for the first time, switch to the log view
    if (andSave) {
      setIsTestingMode(true);
      setTestResult(null);
      setAuthResult(null);
      setError(null);
      setTestLogs([]);
    }

    setAuthResult({ success: false, message: 'Opening Google authorization...' });
    addTestLog('info', 'Opening Google authorization popup...');

    // Generate unique session ID for this authorization attempt
    const sessionId = `blogspot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    addTestLog('info', `Session ID: ${sessionId}`);

    const redirectUri = `${window.location.origin}/auth/blogspot/callback`;
    const clientId = formData.settings.clientId;
    const clientSecret = formData.settings.clientSecret;

    addTestLog('info', `Redirect URI: ${redirectUri}`);
    addTestLog('info', `Client ID: ${clientId}`);

    // Build OAuth URL directly in frontend with session ID
    const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent('https://www.googleapis.com/auth/blogger')}&` +
      `access_type=offline&` +
      `prompt=consent&` +
      `state=${encodeURIComponent(sessionId)}`;

    // Open popup window for OAuth
    const popup = window.open(
      authUrl,
      'blogspot-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      addTestLog('error', 'Failed to open popup window');
      setAuthResult({ success: false, message: 'Failed to open popup window' });
      return;
    }

    let authSuccessful = false;
    setIsAuthorizing(true);

    const handleAuthSuccess = async (code: string) => {
      addTestLog('success', 'Authorization code received from Google');
      addTestLog('info', 'Exchanging authorization code for tokens...');
      try {
        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            code,
            client_id: clientId,
            client_secret: clientSecret,
            redirect_uri: redirectUri,
            grant_type: 'authorization_code',
          }),
        });

        const tokenData = await tokenResponse.json();

        addTestLog('info', `Token response status: ${tokenResponse.status}`);

        if (!tokenResponse.ok) {
          addTestLog('error', 'Token exchange failed', `${tokenData.error_description || tokenData.error || 'Unknown error'}`);
          setAuthResult({ success: false, message: `Token exchange failed: ${tokenData.error_description || tokenData.error || 'Unknown error'}` });
          setIsAuthorizing(false);
          return;
        }

        if (tokenData.refresh_token) {
          setFormData(prev => ({
            ...prev,
            settings: {
              ...prev.settings,
              refreshToken: tokenData.refresh_token
            }
          }));

          if (andSave) {
            addTestLog('info', 'Saving connection...');
            const payload = {
              ...formData,
              iconUrl: selectedTypeDetails?.iconUrl,
              settings: { ...formData.settings, refreshToken: tokenData.refresh_token }
            };

            // Check if we're updating an existing connection or creating a new one
            if (connectionData && 'id' in connectionData) {
              await apiClient.put(`/api/connections/${connectionData.id}`, payload);
              addTestLog('success', 'Connection updated successfully!');
            } else {
              await apiClient.post('/api/connections', payload);
              addTestLog('success', 'Connection saved successfully!');
            }

            setTimeout(() => {
              onSave();
            }, 1500);
          } else {
            addTestLog('success', 'Authorization successful!');
            setAuthResult({ success: true, message: 'Authorization successful!' });
            setTestResult({ success: true, message: 'BlogSpot authorization completed successfully!' });
          }
        } else {
          addTestLog('error', 'Failed to get refresh token from Google');
          setAuthResult({ success: false, message: 'Failed to get refresh token from Google' });
        }
      } catch (error) {
        addTestLog('error', 'Failed to exchange authorization code', error instanceof Error ? error.message : 'Unknown error');
        setAuthResult({ success: false, message: 'Failed to exchange authorization code' });
      }

      setIsAuthorizing(false);
      setIsTesting(false);
    };

    // Check localStorage periodically for auth result
    const checkAuthResult = () => {
      try {
        const storedResult = localStorage.getItem('blogspot_auth_result');
        // addTestLog('info', `Checking localStorage: ${storedResult ? 'found data' : 'no data'}`);

        if (storedResult) {
          const authData = JSON.parse(storedResult);
          addTestLog('info', `Stored auth data: ${JSON.stringify(authData)}`);
          addTestLog('info', `Expected session ID: ${sessionId}`);
          addTestLog('info', `Stored session ID: ${authData.sessionId}`);
          addTestLog('info', `Session ID match: ${authData.sessionId === sessionId}`);
          addTestLog('info', `Type match: ${authData.type === 'BLOGSPOT_AUTH_SUCCESS'}`);
          addTestLog('info', `Timestamp check: ${authData.timestamp > Date.now() - 300000} (stored: ${authData.timestamp}, now: ${Date.now()})`);

          if (authData.type === 'BLOGSPOT_AUTH_SUCCESS' &&
            authData.sessionId === sessionId &&
            authData.timestamp > Date.now() - 300000) {
            localStorage.removeItem('blogspot_auth_result');
            authSuccessful = true;
            addTestLog('success', 'Received authorization via localStorage fallback');
            popup.close();
            handleAuthSuccess(authData.code);
            return true;
          } else {
            addTestLog('warning', 'Found possible staled data found but conditions not met');
            // Remove incorrect/outdated localStorage data
            localStorage.removeItem('blogspot_auth_result');
          }
        }
      } catch (e) {
        addTestLog('error', `Error checking localStorage: ${e}`);
        console.error('Error checking localStorage:', e);
        // Remove corrupted localStorage data
        localStorage.removeItem('blogspot_auth_result');
      }
      return false;
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'BLOGSPOT_AUTH_SUCCESS') {
        const { code } = event.data;
        authSuccessful = true;
        popup.close();
        handleAuthSuccess(code);
      }
    };

    window.addEventListener('message', handleMessage);

    let pollCount = 0;
    const maxPollAttempts = 300; // 5 minutes at 1-second intervals

    // Combined polling for both localStorage and popup status
    const combinedInterval = setInterval(() => {
      pollCount++;
      if (pollCount % 10 === 0) {
        console.log(`Polling attempt ${pollCount}/300`)
        // addTestLog('info', 'Polling status ...');
      }

      // First priority: Check localStorage for auth result
      if (checkAuthResult()) {
        addTestLog('success', 'Data found and check succeeded, stopping polling');
        clearInterval(combinedInterval);
        window.removeEventListener('message', handleMessage);
        return;
      }

      // Second priority: Check if we've exceeded max polling time
      if (pollCount >= maxPollAttempts) {
        clearInterval(combinedInterval);
        window.removeEventListener('message', handleMessage);
        if (!authSuccessful) {
          addTestLog('error', 'Authorization timeout - please try again');
          setAuthResult({ success: false, message: 'Authorization timeout' });
          setIsTesting(false);
        }
        setIsAuthorizing(false);
        return;
      }
      // Third priority: Check popup status (but continue polling localStorage)
      // Only treat as cancelled if popup is closed AND we've waited much longer
      if (popup.closed && pollCount > 60) { // Wait at least 60 seconds before considering cancellation
        // addTestLog('info', 'Popup appears closed, but continuing localStorage polling...');
        // Don't stop the interval - keep polling localStorage
        console.log('Popup appears closed, but continuing local storage polling ...')
      }
    }, 1000);
  };

  const handleWordPressAuth = async (andSave: boolean = false) => {
    if (!formData.settings?.clientId || !formData.settings?.clientSecret) {
      setAuthResult({ success: false, message: 'Please enter Client ID and Client Secret first' });
      return;
    }

    // When connecting for the first time, switch to the log view
    if (andSave) {
      setIsTestingMode(true);
      setTestResult(null);
      setAuthResult(null);
      setError(null);
      setTestLogs([]);
    }

    setAuthResult({ success: false, message: 'Opening WordPress.com authorization...' });
    addTestLog('info', 'Opening WordPress.com authorization popup...');

    // Generate unique session ID for this authorization attempt
    const sessionId = `wordpress_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    addTestLog('info', `Session ID: ${sessionId}`);

    const redirectUri = `${window.location.origin}/auth/wordpress/callback`;
    const clientId = formData.settings.clientId;
    const clientSecret = formData.settings.clientSecret;

    addTestLog('info', `Redirect URI: ${redirectUri}`);
    addTestLog('info', `Client ID: ${clientId}`);

    // Build WordPress OAuth URL
    // According to docs: scope is space-separated, but examples show comma-separated
    // Using 'global' scope for full access (includes sites, posts, media, etc.)
    const authUrl = `https://public-api.wordpress.com/oauth2/authorize?` +
      `client_id=${encodeURIComponent(clientId)}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=code&` +
      `scope=global&` +
      `state=${encodeURIComponent(sessionId)}`;

    // Open popup window for OAuth
    const popup = window.open(
      authUrl,
      'wordpress-auth',
      'width=500,height=600,scrollbars=yes,resizable=yes'
    );

    if (!popup) {
      addTestLog('error', 'Failed to open popup window');
      setAuthResult({ success: false, message: 'Failed to open popup window' });
      return;
    }

    let authSuccessful = false;
    let intervalId: NodeJS.Timeout | null = null;
    setIsAuthorizing(true);

    const handleAuthSuccess = async (code: string) => {
      // Prevent multiple calls
      if (authSuccessful) {
        addTestLog('info', 'Auth already processed, ignoring duplicate call');
        return;
      }
      authSuccessful = true;

      // Stop polling immediately
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      window.removeEventListener('message', handleMessage);

      addTestLog('success', 'Authorization code received from WordPress.com');
      addTestLog('info', `Code: ${code.substring(0, 10)}...`);
      addTestLog('info', 'Exchanging authorization code for access token...');
      addTestLog('info', `Redirect URI being sent: ${redirectUri}`);

      try {
        const tokenResponse = await apiClient.post('/api/connections/wordpress/exchange-token', {
          code,
          clientId,
          clientSecret,
          redirectUri
        });

        if (!tokenResponse.data.accessToken) {
          throw new Error('No access token received');
        }

        addTestLog('success', 'Access token received successfully');

        // Check if multiple sites are available
        if (tokenResponse.data.sites && tokenResponse.data.sites.length > 0) {
          addTestLog('info', `Found ${tokenResponse.data.sites.length} site(s)`);
          // Store access token and sites for selection
          setFormData(prev => ({
            ...prev,
            settings: {
              ...prev.settings,
              accessToken: tokenResponse.data.accessToken
            }
          }));
          setAvailableSites(tokenResponse.data.sites);
          setAuthResult({
            success: true,
            message: `Authorization successful! Click OK to continue.`
          });
          setIsAuthorizing(false);
          setIsTesting(false);
          // Don't show site selector yet - wait for user to click OK
          return;
        }

        // Fallback: use default site info if no sites array
        addTestLog('info', `Site: ${tokenResponse.data.siteName}`);
        addTestLog('info', `Site ID: ${tokenResponse.data.siteId}`);

        // Update form data with the tokens and site info
        setFormData(prev => ({
          ...prev,
          settings: {
            ...prev.settings,
            accessToken: tokenResponse.data.accessToken,
            siteId: tokenResponse.data.siteId,
            siteName: tokenResponse.data.siteName,
            siteUrl: tokenResponse.data.siteUrl
          }
        }));

        setAuthResult({
          success: true,
          message: `Successfully connected to ${tokenResponse.data.siteName}`
        });

        if (andSave) {
          addTestLog('info', 'Saving connection...');
          const payload = {
            ...formData,
            settings: {
              ...formData.settings,
              accessToken: tokenResponse.data.accessToken,
              siteId: tokenResponse.data.siteId,
              siteName: tokenResponse.data.siteName,
              siteUrl: tokenResponse.data.siteUrl
            },
            iconUrl: selectedTypeDetails?.iconUrl,
            platformName: tokenResponse.data.siteName
          };

          if (connectionData && 'id' in connectionData) {
            await apiClient.put(`/api/connections/${connectionData.id}`, payload);
            addTestLog('success', 'Connection updated successfully!');
          } else {
            await apiClient.post('/api/connections', payload);
            addTestLog('success', 'Connection saved successfully!');
          }

          setTimeout(() => {
            onSave();
            onClose();
          }, 1500);
        }

      } catch (error: any) {
        const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
        addTestLog('error', `Token exchange failed: ${errorMessage}`);
        addTestLog('error', `Full error: ${JSON.stringify(error.response?.data || error)}`);
        setAuthResult({
          success: false,
          message: `Token exchange failed: ${errorMessage}`
        });
      } finally {
        setIsAuthorizing(false);
        setIsTesting(false);
      }
    };

    // Check localStorage for auth result
    const checkAuthResult = () => {
      if (authSuccessful) return false; // Already processed

      try {
        const stored = localStorage.getItem('wordpress_auth_result');
        if (stored) {
          const authData = JSON.parse(stored);
          if (authData.sessionId === sessionId && authData.code) {
            addTestLog('success', 'Authorization result found in localStorage');
            localStorage.removeItem('wordpress_auth_result');
            handleAuthSuccess(authData.code);
            return true;
          }
        }
      } catch (e) {
        addTestLog('error', `Error checking localStorage: ${e}`);
        localStorage.removeItem('wordpress_auth_result');
      }
      return false;
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data.type === 'WORDPRESS_AUTH_SUCCESS') {
        const { code } = event.data;
        popup.close();
        handleAuthSuccess(code);
      }
    };

    window.addEventListener('message', handleMessage);

    let pollCount = 0;
    const maxPollAttempts = 300; // 5 minutes

    intervalId = setInterval(() => {
      pollCount++;
      if (pollCount % 10 === 0) {
        console.log(`Polling attempt ${pollCount}/300`)
        addTestLog('info', 'Polling status ...');
      }

      if (checkAuthResult()) {
        // addTestLog('success', 'localStorage check succeeded, stopping polling');
        // checkAuthResult already calls handleAuthSuccess which clears the interval
        return;
      }

      if (pollCount >= maxPollAttempts) {
        if (intervalId) clearInterval(intervalId);
        window.removeEventListener('message', handleMessage);
        if (!authSuccessful) {
          addTestLog('error', 'Authorization timeout - please try again');
          setAuthResult({ success: false, message: 'Authorization timeout' });
          setIsTesting(false);
          setIsAuthorizing(false);
        }
        return;
      }

      if (popup.closed && !authSuccessful && pollCount > 60) {
        addTestLog('info', 'Popup appears closed, but continuing localStorage polling...');
      }
    }, 1000);
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10 focus:outline-none" onClose={() => { }}>
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          <div className="fixed inset-0 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
                  <div className="flex items-center justify-between mb-6">
                    <Dialog.Title as="h3" className="text-xl font-semibold text-gray-900">
                      {isTestingMode
                        ? `Verifying ${selectedTypeDetails?.displayName || formData.platformType || 'Connection'}`
                        : `${connectionData && 'id' in connectionData ? 'Edit' : 'Add'} Connection`
                      }
                    </Dialog.Title>
                    {!isTestingMode && (
                      <button
                        onClick={openHelpPage}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                        type="button"
                      >
                        <QuestionMarkCircleIcon className="h-4 w-4" />
                        Help
                      </button>
                    )}
                    {isTestingMode && (
                      <button
                        onClick={copyLogsToClipboard}
                        disabled={testLogs.length === 0}
                        className={`p-2 rounded-md transition-colors ${testLogs.length === 0
                          ? 'text-gray-300 cursor-not-allowed'
                          : copySuccess
                            ? 'text-green-600 bg-green-50'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                          }`}
                        title="Copy logs to clipboard"
                      >
                        <ClipboardDocumentIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {isTestingMode ? (
                    <div className="bg-gray-50 rounded-lg p-4 h-96 overflow-y-auto mb-6 border border-gray-200 shadow-inner">
                      {testLogs.length === 0 ? (
                        <div className="text-gray-500 text-center py-8">
                          {isTesting ? 'Starting connection test...' : 'No logs yet'}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {testLogs.map((log) => (
                            <div key={log.id} className="flex items-start space-x-2">
                              <div className="flex-shrink-0 mt-0.5">
                                {log.level === 'success' && <CheckCircleIcon className="h-4 w-4 text-green-500" />}
                                {log.level === 'error' && <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />}
                                {log.level === 'warning' && <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />}
                                {log.level === 'info' && <InformationCircleIcon className="h-4 w-4 text-blue-500" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-gray-500 font-mono">
                                    {log.timestamp.toLocaleTimeString()}
                                  </span>
                                  <span className={`text-sm ${log.level === 'success' ? 'text-green-700' :
                                    log.level === 'error' ? 'text-red-700' :
                                      log.level === 'warning' ? 'text-yellow-700' :
                                        'text-gray-700'
                                    }`}>
                                    {log.message}
                                  </span>
                                </div>
                                {log.details && (
                                  <div className="mt-1 text-xs text-gray-600 bg-white rounded border border-gray-200 px-2 py-1 font-mono break-all">
                                    {log.details}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {isTesting && (
                        <div className="flex items-center space-x-2 mt-4 p-2 bg-blue-50 rounded border border-blue-100">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <span className="text-sm text-blue-700 font-medium">Testing in progress...</span>
                        </div>
                      )}
                    </div>
                  ) : showSiteSelector ? (
                    /* WordPress Site Selection View */
                    <div className="mt-6 mb-6">
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                          <CheckCircleIcon className="h-10 w-10 text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                          🎉 Authorization Successful!
                        </h3>
                        <p className="text-gray-600">
                          One more step: Select which WordPress.com site you want to connect
                        </p>
                      </div>

                      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                        {availableSites.map((site) => (
                          <button
                            key={site.id}
                            onClick={() => {
                              setSelectedSite(site);
                              handleSiteSelection(site);
                            }}
                            className="w-full text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all group"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 text-lg group-hover:text-blue-700">{site.name}</div>
                                <div className="text-sm text-gray-600 mt-1">{site.url}</div>
                                <div className="text-xs text-gray-500 mt-2 font-mono bg-gray-100 inline-block px-1.5 py-0.5 rounded">ID: {site.id}</div>
                              </div>
                              <div className="bg-white rounded-full p-1 border border-gray-200 group-hover:border-blue-300">
                                <svg className="h-6 w-6 text-gray-400 group-hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-6">
                      <Tab.Group>
                        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-5">
                          <Tab
                            className={({ selected }) =>
                              classNames(
                                'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                selected
                                  ? 'bg-white text-blue-700 shadow'
                                  : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-800'
                              )
                            }
                          >
                            Basic Settings
                          </Tab>
                          <Tab
                            className={({ selected }) =>
                              classNames(
                                'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                                selected
                                  ? 'bg-white text-blue-700 shadow'
                                  : 'text-gray-600 hover:bg-white/[0.12] hover:text-gray-800'
                              )
                            }
                          >
                            Advanced Configuration
                          </Tab>
                        </Tab.List>
                        <Tab.Panels>
                          {/* Basic Settings Tab */}
                          <Tab.Panel className="focus:outline-none">
                            <Fieldset className="space-y-4">
                              <Field>
                                <Label>Platform Type</Label>
                                <Dropdown
                                  options={connectionTypes.map(ct => ({ value: ct.type, label: ct.displayName }))}
                                  value={formData.platformType || ''}
                                  onChange={(value) => handleDropdownChange('platformType', value)}
                                  disabled={!!(connectionData && 'id' in connectionData)}
                                  placeholder="Select a platform"
                                />
                              </Field>
                              <Field>
                                <Label>Display Name</Label>
                                <Input name="platformName" value={formData.platformName || ''} onChange={handleInputChange} placeholder="My Connection" />
                              </Field>

                              {/* Render basic required settings (URLs, Keys, etc) - but filter out 'advanced' looking ones */}
                              {selectedTypeDetails?.requiredSettings.filter(s => !s.hidden && !['headers', 'payload', 'custom'].some(keyword => s.key.includes(keyword))).map(setting => (
                                <Field key={setting.key}>
                                  <Label>{setting.label}</Label>
                                  {setting.key === 'refreshToken' ? (
                                    <div className="space-y-2">
                                      <Input
                                        name={`settings.${setting.key}`}
                                        type={setting.type}
                                        value={formData.settings?.[setting.key] || ''}
                                        onChange={handleInputChange}
                                        placeholder="Auto-generated during first connection test"
                                        disabled
                                      />
                                      <p className="text-sm text-gray-500">
                                        This will be automatically generated when you test the connection.
                                      </p>
                                    </div>
                                  ) : setting.type === 'select' ? (
                                    <Dropdown
                                      value={formData.settings?.[setting.key] || setting.defaultValue || ''}
                                      onChange={(value) => handleSettingChange(setting.key, value)}
                                      options={(setting.options || []).map(opt => ({ value: opt, label: opt }))}
                                      placeholder={`Select ${setting.label}`}
                                    />
                                  ) : setting.type === 'textarea' ? (
                                    <textarea
                                      name={`settings.${setting.key}`}
                                      value={formData.settings?.[setting.key] || ''}
                                      onChange={handleInputChange}
                                      placeholder={setting.placeholder || ''}
                                      rows={4}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                                    />
                                  ) : (
                                    <Input
                                      name={`settings.${setting.key}`}
                                      type={setting.type}
                                      value={formData.settings?.[setting.key] || ''}
                                      onChange={handleInputChange}
                                      placeholder={setting.placeholder || ''}
                                    />
                                  )}
                                </Field>
                              ))}

                              {/* Compact connected site badge at bottom for WordPress OAuth */}
                              {isEditingConnection && formData.platformType === 'wordpress_oauth' && formData.settings?.siteName && (
                                <div className="mt-4 flex items-center gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                                  <div className="flex-shrink-0">
                                    <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-gray-900 truncate">
                                        {formData.settings.siteName}
                                      </span>
                                      <a
                                        href={formData.settings.siteUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:underline"
                                      >
                                        Visit Site
                                      </a>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5 font-mono">
                                      Ref: {formData.settings.siteId}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Fieldset>
                          </Tab.Panel>

                          {/* Advanced Configuration Tab */}
                          <Tab.Panel className="focus:outline-none">
                            <Fieldset className="space-y-4">
                              <p className="text-sm text-gray-500 mb-4 bg-yellow-50 p-3 rounded-md border border-yellow-100 text-yellow-800">
                                <span className="font-semibold">Note:</span> These settings are for advanced users. Leave them empty to use default values.
                              </p>

                              {/* Render advanced settings (headers, payloads, etc) */}
                              {selectedTypeDetails?.requiredSettings.filter(s => !s.hidden && ['headers', 'payload', 'custom'].some(keyword => s.key.includes(keyword))).map(setting => (
                                <Field key={setting.key}>
                                  <Label>{setting.label}</Label>
                                  {setting.type === 'textarea' ? (
                                    <textarea
                                      name={`settings.${setting.key}`}
                                      value={formData.settings?.[setting.key] || ''}
                                      onChange={handleInputChange}
                                      placeholder={setting.placeholder || ''}
                                      rows={10}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs bg-gray-50"
                                    />
                                  ) : (
                                    <Input
                                      name={`settings.${setting.key}`}
                                      type={setting.type}
                                      value={formData.settings?.[setting.key] || ''}
                                      onChange={handleInputChange}
                                      placeholder={setting.placeholder || ''}
                                    />
                                  )}
                                </Field>
                              ))}

                              {/* Create fallback visual if no advanced settings exist */}
                              {selectedTypeDetails?.requiredSettings.filter(s => !s.hidden && ['headers', 'payload', 'custom'].some(keyword => s.key.includes(keyword))).length === 0 && (
                                <div className="text-center py-10 text-gray-400 italic">
                                  No advanced settings available for this connection type.
                                </div>
                              )}
                            </Fieldset>
                          </Tab.Panel>
                        </Tab.Panels>
                      </Tab.Group>
                    </div>
                  )}

                  <div className="mt-8 flex justify-between items-center pt-5 border-t border-gray-100">
                    {isTestingMode ? (
                      <div className="flex gap-3 w-full justify-end">
                        {isTesting && (
                          <Button
                            plain
                            className="bg-red-50 text-red-700 hover:bg-red-100"
                            onClick={() => {
                              setIsTesting(false);
                              setIsAuthorizing(false);
                              addTestLog('warning', 'Testing stopped by user');
                            }}
                          >
                            Stop Testing
                          </Button>
                        )}
                        <Button
                          color="blue"
                          className="px-6"
                          onClick={() => {
                            // Check if we have sites to select from
                            if (availableSites.length > 0 && formData.platformType === 'wordpress_oauth') {
                              // Transition to site selection view
                              setShowSiteSelector(true);
                              setIsTestingMode(false);
                            } else {
                              // Normal OK behavior - close test view
                              setIsAuthorizing(false);
                              setIsTestingMode(false);
                              setTestLogs([]);
                              setAuthResult(null);
                              setTestResult(null);
                              setError(null);
                            }
                          }}
                        >
                          Close Logs
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div>
                          {/* Test Connection button for token/application_password OR for editing OAuth connections */}
                          {(selectedTypeDetails?.authType === 'token' || selectedTypeDetails?.authType === 'application_password') && (
                            <Button
                              plain
                              className="text-gray-600 hover:bg-gray-100 border border-gray-200"
                              onClick={handleTestConnection}
                              disabled={isTesting}
                            >
                              {isTesting ? 'Testing...' : 'Test Connection'}
                            </Button>
                          )}
                          {/* For OAuth connections being edited, show Re-authorize button */}
                          {selectedTypeDetails?.authType === 'oauth' && isEditingConnection && (
                            <Button
                              plain
                              className="text-gray-600 hover:bg-gray-100 border border-gray-200"
                              onClick={handleTestConnection}
                              disabled={isTesting || isAuthorizing}
                            >
                              {isTesting || isAuthorizing ? 'Testing...' : 'Test / Re-authorize'}
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-3">
                          <Button
                            plain
                            className="text-gray-600 hover:text-gray-800"
                            onClick={onClose}
                          >
                            Cancel
                          </Button>
                          {selectedTypeDetails?.authType === 'oauth' && !('id' in (connectionData || {})) ? (
                            <Button
                              color="blue"
                              className="shadow-sm"
                              onClick={() => {
                                if (formData.platformType === 'blogspot') {
                                  handleBlogSpotAuth(true);
                                } else if (formData.platformType === 'wordpress_oauth') {
                                  handleWordPressAuth(true);
                                }
                              }}
                              disabled={isSaving || isAuthorizing}
                            >
                              {isAuthorizing || isSaving ? 'Connecting...' : 'Connect & Authorize'}
                            </Button>
                          ) : (
                            <Button
                              color="blue"
                              className="shadow-sm"
                              onClick={handleSave}
                              disabled={isSaving}
                            >
                              {isSaving ? 'Saving...' : 'Save Connection'}
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {!isTestingMode && (
                    <>
                      {authResult && authResult.message && <p className={`mt-3 text-sm text-right ${authResult.success ? 'text-green-600' : 'text-red-500'}`}>{authResult.message}</p>}
                      {testResult && testResult.message && <p className={`mt-3 text-sm text-right ${testResult.success ? 'text-green-600' : 'text-red-500'}`}>{testResult.message}</p>}
                      {error && <p className="text-red-500 mt-3 text-sm text-right bg-red-50 p-2 rounded">{error}</p>}
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
